<script setup lang="ts">
import type { Status } from "@/api/employeeMovements";
import { useGetVisit, type VisitWithFields } from "@/api/visits";
import CoreAvatar from "@/components/common/CoreAvatar/CoreAvatar.vue";
import StatusSelectorVisitor from "@/components/common/StatusSelector/StatusSelectorVisitor.vue";
import TimelinesDisplay from "@/components/common/TimelinesDisplay/TimelinesDisplay.vue";
import { useRootStore } from "@/stores";
import useVisitStore from "@/stores/visit";
import { ElDrawer, ElAlert } from "element-plus";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import EditVisitorTimelineDrawerSkeleton from "./EditVisitorTimelineDrawerSkeleton.vue";
import EditVisitorTimelineForm from "./EditVisitorTimelineForm.vue";
import { useI18n } from "vue-i18n";
import { SOCKET_EVENT } from "@/stores/constants";
import { useLiveUpdates } from "@/composables/liveUpdates";

export interface Props {
  visible: boolean;
  visitId?: string;
  selectedVisit?: VisitWithFields;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "status-changed", status: Status): void;
}>();

const rootStore = useRootStore();
const { hasUnsavedFormChanges } = storeToRefs(rootStore);
const { t } = useI18n();
const { visitLoadings } = useVisitStore();

const visitFormRef = ref<InstanceType<typeof EditVisitorTimelineForm> | null>();

const {
  data: visit,
  isFetching: isLoading,
  refetch,
} = useGetVisit(computed(() => props.visitId || ""));

useLiveUpdates({
  [SOCKET_EVENT.VISIT_CLOSE]: {
    fn: (visitor) => {
      if (visitor.pk !== props.visitId) return;

      refetch();
    },
  },
});
</script>

<template>
  <el-drawer
    :model-value="visible"
    direction="rtl"
    size="100%"
    :with-header="false"
    destroy-on-close
    :close-on-click-modal="!hasUnsavedFormChanges"
    :close-on-press-escape="!hasUnsavedFormChanges"
    @closed="emit('close')"
  >
    <slot />
    <EditVisitorTimelineDrawerSkeleton :loading="isLoading">
      <div class="header">
        <CoreAvatar
          :src="visit?.photoIn"
          :is-anonymized="visitFormRef?.isAnonymized"
          :icon="visitFormRef?.isAnonymized ? ['fal', 'eye-slash'] : undefined"
          size="large"
          class="avatar"
        />
        <div class="details">
          <h3 class="break--line-word">{{ visit?.fullName }}</h3>
          <TimelinesDisplay
            v-if="visit?.id"
            :movement="visit"
            type="visitor"
          />
          <StatusSelectorVisitor
            v-if="visit && !visitFormRef?.isAnonymized"
            :visit="visit"
            :loading="visitLoadings[visit.id]"
            @change="emit('status-changed', $event)"
          />
        </div>
      </div>
      <el-alert
        v-if="visit?.randomSelection"
        :closable="false"
        show-icon
        type="info"
        class="randomised-alert"
        :title="t('visitors.timeline.randomisedAlert')"
      />
    </EditVisitorTimelineDrawerSkeleton>
    <EditVisitorTimelineForm
      ref="visitFormRef"
      :visit="visit"
      :is-loading-visit="isLoading"
      @close="emit('close')"
    />
  </el-drawer>
</template>

<style lang="css" scoped>
.header {
  display: flex;
  align-items: center;
  margin-top: 25px;
}

.avatar {
  flex: none;
}

.details {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 7px;
  justify-content: center;
  height: 100%;
  margin-left: 20px;
}

.randomised-alert {
  margin-top: 20px;
}
</style>
